import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, CircularProgress } from '@mui/material';
import SessionService from '../../api_handlers/services/SessionService.ts';

// Define types for the leaderboard response
interface Team {
  teamName: string;
  totalCredits: number;
  percentage: number;
  teamId: number;
}

const GameViewLeaderBoard = ({ sessionId }: { sessionId: string }) => {
  const [leaderboard, setLeaderboard] = useState<Team[] | null>(null); 
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchLeaderBoard = useCallback(async () => {
    setLoading(true); // Start loading
    try {
      const data = await SessionService.getLeaderboard(sessionId);

      // Ensure data is in the expected structure
      const teamList = data?.data?.leaderboard?.teamList;
      if (teamList) {
        setLeaderboard(teamList); // Set teamList directly
        setLoading(false); // Stop loading after data is fetched
      } else {
        setError('Leaderboard data is missing the expected structure.');
        setLoading(false);
      }
    } catch (error) {
      setError('Error fetching leaderboard data.');
      setLoading(false);
      console.error('Error fetching leaderboard data:', error);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchLeaderBoard();
  }, [fetchLeaderBoard]);

  // Handle the loading and error states
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress sx={{ color: '#FFD700' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="h6" align="center" sx={{
        color: '#FF6B6B',
        fontFamily: "'Philosopher', sans-serif",
      }}>
        {error}
      </Typography>
    );
  }

  if (!leaderboard) {
    return (
      <Typography variant="h6" align="center" sx={{
        color: '#FFA500',
        fontFamily: "'Philosopher', sans-serif",
      }}>
        Loading leaderboard...
      </Typography>
    );
  }

  return (
    <div>
      <Typography variant="h5" gutterBottom align="center" sx={{
        fontFamily: "'Cinzel', serif",
        color: '#FFD700',
        fontWeight: 700,
        textShadow: '0.15rem 0.15rem 0.3rem rgba(0,0,0,0.9), 0 0 0.8rem rgba(255, 215, 0, 0.6)',
        mb: 3,
      }}>
        🏆 Game Leaderboard
      </Typography>

      {/* Table layout for leaderboard */}
      <TableContainer component={Paper} sx={{
        boxShadow: '0 0.5rem 1.5rem rgba(0, 0, 0, 0.5)',
        borderRadius: '1rem',
        border: '0.2rem solid rgba(218, 165, 32, 0.6)',
        background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.3) 0%, rgba(101, 67, 33, 0.25) 100%)',
      }}>
        <Table>
          <TableHead>
            <TableRow sx={{
              background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.5) 0%, rgba(101, 67, 33, 0.4) 100%)',
            }}>
              <TableCell align="center" sx={{
                fontFamily: "'Cinzel', serif",
                color: '#FFD700',
                fontWeight: 700,
                fontSize: '1.05rem',
                textShadow: '0.1rem 0.1rem 0.2rem rgba(0,0,0,0.8)',
                borderBottom: '0.15rem solid rgba(218, 165, 32, 0.6)',
              }}>Rank</TableCell>
              <TableCell align="center" sx={{
                fontFamily: "'Cinzel', serif",
                color: '#FFD700',
                fontWeight: 700,
                fontSize: '1.05rem',
                textShadow: '0.1rem 0.1rem 0.2rem rgba(0,0,0,0.8)',
                borderBottom: '0.15rem solid rgba(218, 165, 32, 0.6)',
              }}>Team</TableCell>
              <TableCell align="center" sx={{
                fontFamily: "'Cinzel', serif",
                color: '#FFD700',
                fontWeight: 700,
                fontSize: '1.05rem',
                textShadow: '0.1rem 0.1rem 0.2rem rgba(0,0,0,0.8)',
                borderBottom: '0.15rem solid rgba(218, 165, 32, 0.6)',
              }}>Total Credits</TableCell>
              <TableCell align="center" sx={{
                fontFamily: "'Cinzel', serif",
                color: '#FFD700',
                fontWeight: 700,
                fontSize: '1.05rem',
                textShadow: '0.1rem 0.1rem 0.2rem rgba(0,0,0,0.8)',
                borderBottom: '0.15rem solid rgba(218, 165, 32, 0.6)',
              }}>Shumas Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaderboard.map((team, index) => (
              <TableRow key={team.teamId} sx={{
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(218, 165, 32, 0.2)',
                },
                background: index === 0 ? 'rgba(255, 215, 0, 0.15)' : 'transparent',
              }}>
                <TableCell align="center" sx={{
                  fontFamily: "'Cinzel', serif",
                  color: index === 0 ? '#FFD700' : '#FFA500',
                  fontWeight: 700,
                  fontSize: index === 0 ? '1.5rem' : '1.1rem',
                  borderBottom: '0.1rem solid rgba(218, 165, 32, 0.3)',
                }}>
                  {index === 0 ? '👑' : index + 1}
                </TableCell>
                <TableCell align="center" sx={{
                  borderBottom: '0.1rem solid rgba(218, 165, 32, 0.3)',
                }}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <img
                      src={`/img/team-logo/${team.teamName}.png`}
                      alt={team.teamName}
                      style={{
                        width: index === 0 ? 40 : 30,
                        height: index === 0 ? 40 : 30,
                        borderRadius: '50%',
                        border: index === 0 ? '0.15rem solid #FFD700' : 'none',
                        boxShadow: index === 0 ? '0 0 0.75rem rgba(255, 215, 0, 0.6)' : 'none',
                      }}
                    />
                    <Typography sx={{
                      fontFamily: "'Philosopher', sans-serif",
                      color: index === 0 ? '#FFD700' : '#FFA500',
                      fontWeight: index === 0 ? 700 : 600,
                      fontSize: index === 0 ? '1.1rem' : '1rem',
                    }}>
                      {team.teamName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{
                  fontFamily: "'Philosopher', sans-serif",
                  color: '#FFA500',
                  fontWeight: 600,
                  fontSize: '1rem',
                  borderBottom: '0.1rem solid rgba(218, 165, 32, 0.3)',
                }}>{team.totalCredits}</TableCell>
                <TableCell align="center" sx={{
                  fontFamily: "'Philosopher', sans-serif",
                  color: index === 0 ? '#FFD700' : '#FFA500',
                  fontWeight: index === 0 ? 700 : 600,
                  fontSize: index === 0 ? '1.1rem' : '1rem',
                  borderBottom: '0.1rem solid rgba(218, 165, 32, 0.3)',
                }}>{team.percentage}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default GameViewLeaderBoard;
